import { useCallback, useState } from "react";

import { useRouterState } from "@tanstack/react-router";

import { shouldWarnOnNavigation } from "@components/RecordingDock/dockVisibility";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";

interface UseNavigationWarningReturn {
  showWarningModal: boolean;
  pendingNavigation: (() => void) | null;
  setShowWarningModal: (show: boolean) => void;
  setPendingNavigation: (navigation: (() => void) | null) => void;
  handleNavigationWithWarning: (
    navigationFn: () => void,
    targetPath?: string
  ) => void;
  handleConfirmNavigation: () => void;
  handleCancelNavigation: () => void;
  isOnReportPreview: boolean;
  /**
   * True when the currently-open warning is the recording case-boundary guard
   * rather than the report-preview unsaved guard — lets the consumer
   * render the two-button recording modal with the examinee's name.
   */
  isRecordingWarning: boolean;
  /** Examinee whose recording is at risk (for the recording warning copy). */
  recordingExamineeName: string | null;
}

export const useNavigationWarning = (): UseNavigationWarningReturn => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(
    null
  );
  const [isRecordingWarning, setIsRecordingWarning] = useState(false);

  // Recording case-boundary state. Read imperatively at click time so
  // the guard reflects the live session without re-subscribing every render.
  const recordingExamineeName = useRecordingDock((s) => s.examineeName);

  // Check if we're currently on the ReportPreview page
  const isOnReportPreview = pathname.includes("/preview");

  const handleNavigationWithWarning = useCallback(
    (navigationFn: () => void, targetPath?: string) => {
      const dock = useRecordingDock.getState();
      // A live recording leaving its owning case must be confirmed first.
      // Without a known target we assume the click leaves the case (sidebar
      // links always route elsewhere), which is the safe default; the case's
      // own tab bar handles in-case navigation.
      const recordingLeavesCase =
        dock.isActive() &&
        (targetPath === undefined
          ? true
          : shouldWarnOnNavigation({
              isActive: true,
              recordingCaseNumber: dock.caseNumber,
              currentPath: pathname,
              targetPath,
            }));

      if (recordingLeavesCase) {
        setIsRecordingWarning(true);
        setPendingNavigation(() => navigationFn);
        setShowWarningModal(true);
        return;
      }

      if (isOnReportPreview) {
        setIsRecordingWarning(false);
        setPendingNavigation(() => navigationFn);
        setShowWarningModal(true);
      } else {
        navigationFn();
      }
    },
    [isOnReportPreview, pathname]
  );

  const handleConfirmNavigation = useCallback(() => {
    // Confirming a recording warning means "finish and leave" — stop + save the
    // live session before proceeding so nothing crosses the case boundary.
    if (isRecordingWarning) {
      useRecordingDock.getState().controls.stop();
    }
    setShowWarningModal(false);
    setIsRecordingWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [isRecordingWarning, pendingNavigation]);

  const handleCancelNavigation = useCallback(() => {
    setShowWarningModal(false);
    setIsRecordingWarning(false);
    setPendingNavigation(null);
  }, []);

  return {
    showWarningModal,
    pendingNavigation,
    setShowWarningModal,
    setPendingNavigation,
    handleNavigationWithWarning,
    handleConfirmNavigation,
    handleCancelNavigation,
    isOnReportPreview,
    isRecordingWarning,
    recordingExamineeName,
  };
};
